import {
  cancelRentChange,
  createRenewal,
  createRentChange,
  useListRenewals,
  useListRentChanges,
  withdrawRenewal,
  type RentChangeOut,
  type RenewalOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import { optional, required, type Field } from '../../components/FormFields';
import { TablePager, useTablePage } from '../../components/TablePager';
import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDate, formatMoney } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { RENEWAL_STATUS } from './labels';
import { useLeaseOptions } from './useLeaseOptions';

const untilText = (end: string | null) => (end ? formatDate(end) : 'Month to Month');

export function RenewalsPage() {
  const canManage = useCan('notices.manage');
  const queryClient = useQueryClient();
  const paging = useTablePage();
  const query = useListRenewals(paging.params);
  const offers = okItems(query.data);
  const changes = okData(useListRentChanges().data)?.items ?? [];
  const leases = useLeaseOptions(true);
  const [dialog, setDialog] = useState<'offer' | 'rent' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function act(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  const offerFields: Field[] = [
    { name: 'lease_id', label: 'Lease', type: 'select', required: true, options: leases },
    { name: 'new_rent', label: 'New Monthly Rent', type: 'number', required: true },
    {
      name: 'new_end_date',
      label: 'New End Date',
      type: 'date',
      helperText: 'Leave blank for month to month',
    },
    { name: 'offer_expires_on', label: 'Offer Open Until', type: 'date', required: true },
    { name: 'message', label: 'Message', type: 'multiline' },
  ];
  const rentFields: Field[] = [
    { name: 'lease_id', label: 'Lease', type: 'select', required: true, options: leases },
    { name: 'new_rent', label: 'New Monthly Rent', type: 'number', required: true },
    {
      name: 'effective_on',
      label: 'Starts On',
      type: 'date',
      required: true,
      helperText: 'The first of a month. Check the notice your state and lease require.',
    },
  ];

  const offerColumns: DataColumn<RenewalOut>[] = [
    { label: 'Rental', render: (o) => o.place },
    { label: 'Residents', render: (o) => o.residents || '–' },
    {
      label: 'Rent',
      render: (o) => `${formatMoney(o.current_rent)} to ${formatMoney(o.new_rent)}`,
    },
    {
      label: 'Term Ends',
      render: (o) => `${untilText(o.current_end_date)} to ${untilText(o.new_end_date)}`,
    },
    { label: 'Open Until', render: (o) => formatDate(o.offer_expires_on) },
    {
      label: 'Status',
      render: (o) =>
        o.expired ? (
          <Chip size="small" label="Expired" />
        ) : (
          <Chip
            size="small"
            color={RENEWAL_STATUS[o.status].color}
            label={RENEWAL_STATUS[o.status].label}
            title={o.response_note ?? undefined}
          />
        ),
    },
    {
      label: 'Actions',
      align: 'right',
      render: (o) =>
        canManage &&
        o.status === 'sent' && (
          <Button size="small" color="error" onClick={() => void act(() => withdrawRenewal(o.id))}>
            Withdraw
          </Button>
        ),
    },
  ];
  const changeColumns: DataColumn<RentChangeOut>[] = [
    { label: 'Rental', render: (c) => c.place },
    { label: 'Rent', render: (c) => `${formatMoney(c.old_rent)} to ${formatMoney(c.new_rent)}` },
    { label: 'Starts', render: (c) => formatDate(c.effective_on) },
    {
      label: 'Status',
      render: (c) =>
        c.canceled_at ? (
          <Chip size="small" label="Canceled" />
        ) : c.applied_at ? (
          <Chip size="small" color="success" label="In Effect" />
        ) : (
          <Chip size="small" color="info" label={`${c.days_of_notice} Days Away`} />
        ),
    },
    {
      label: 'Actions',
      align: 'right',
      render: (c) =>
        canManage &&
        !c.applied_at &&
        !c.canceled_at && (
          <Button size="small" color="error" onClick={() => void act(() => cancelRentChange(c.id))}>
            Cancel
          </Button>
        ),
    },
  ];

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flexGrow: 1 }}>
          <Typography variant="h5">Renewals</Typography>
          <Typography color="text.secondary">
            Offer new terms, and residents accept or decline in their portal. New rent starts on the
            first of the month after the current term.
          </Typography>
        </div>
        {canManage && (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setDialog('rent')}>
              Change Rent
            </Button>
            <Button variant="contained" onClick={() => setDialog('offer')}>
              New Offer
            </Button>
          </Stack>
        )}
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      <Stack spacing={1}>
        <Typography variant="h6">Renewal Offers</Typography>
        {offers.length === 0 ? (
          <Typography color="text.secondary">No offers yet.</Typography>
        ) : (
          <>
            <DataTable columns={offerColumns} rows={offers} rowKey={(o) => o.id} />
            <TablePager paging={paging} total={okData(query.data)?.total ?? 0} />
          </>
        )}
      </Stack>
      <Stack spacing={1}>
        <Typography variant="h6">Rent Changes</Typography>
        {changes.length === 0 ? (
          <Typography color="text.secondary">No rent changes yet.</Typography>
        ) : (
          <DataTable columns={changeColumns} rows={changes} rowKey={(c) => c.id} />
        )}
      </Stack>
      {dialog === 'offer' && (
        <FormDialog
          title="New Renewal Offer"
          intro="This emails the residents on the lease straight away. Accepting extends the lease in the app; a signed renewal document is still up to you for now."
          fields={offerFields}
          initial={{
            lease_id: '',
            new_rent: '',
            new_end_date: '',
            offer_expires_on: '',
            message: '',
          }}
          submitLabel="Send Offer"
          onSubmit={(values) =>
            createRenewal({
              lease_id: Number(required(values, 'lease_id')),
              new_rent: required(values, 'new_rent'),
              new_end_date: optional(values, 'new_end_date'),
              offer_expires_on: required(values, 'offer_expires_on'),
              message: optional(values, 'message'),
            })
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'rent' && (
        <FormDialog
          title="Change Rent"
          intro="This schedules the new rent and drafts a notice under Notices. It doesn't tell the residents until you send that notice."
          fields={rentFields}
          initial={{ lease_id: '', new_rent: '', effective_on: '' }}
          submitLabel="Schedule Change"
          onSubmit={async (values) => {
            await createRentChange(Number(required(values, 'lease_id')), {
              new_rent: required(values, 'new_rent'),
              effective_on: required(values, 'effective_on'),
            });
            setNotice('Scheduled. Read and send the draft notice under Notices.');
          }}
          onClose={() => setDialog(null)}
        />
      )}
    </Stack>
  );
}
